import React, { useEffect, useMemo, useState } from 'react'
import { CircleDollarSign, Goal, Trophy, TrendingUp } from 'lucide-react'
import { Tabs } from '../FbUI/Tabs'
import { useQuery } from '@tanstack/react-query'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import {
  getHomepageStats,
  getPerformanceStats,
  getPipelineValueStats,
  type HomepageStats,
  type PerformanceStats,
  type PerformanceRange,
  type PipelineValueStats,
} from '../../services/recommendations_api'
import {
  formatInteger,
  formatPercentage,
  formatCurrency,
} from '../../utils/formatting'
import { getChartColor } from '../../utils/chartColors'
import { ProgressChart } from '../NewCompanyPage/Sidebar/shared/ProgressChart'
import { PerformanceChart } from './PerformanceChart'
import type { PeriodType } from './PeriodToggle'
import { RangeSelector } from './RangeSelector'

/** Shared card container styles used across homepage dashboard cards. */
const CARD_CONTAINER_STYLES = 'bg-surface rounded-lg border border-line p-4'

interface PerformanceDashboardCardProps {
  /** Optional user ID for superusers to view another user's performance. */
  userId?: number
  /** Optional additional CSS class names. */
  className?: string
}

/**
 * Skeleton loader for the performance dashboard.
 *
 * Mirrors the exact layout of the loaded card to prevent content shift:
 * header → 4 metric cards → chart tabs + bar chart area.
 */
const PerformanceDashboardSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={cn(CARD_CONTAINER_STYLES, className)}>
    <div className="animate-pulse">
      {/* Header: range label + range selector dropdown / "Monthly" badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="h-4 w-20 bg-surface-disabled rounded" />
          {/* RangeSelector (ReactSelect) approximation */}
          <div className="h-[38px] w-28 bg-surface-disabled rounded" />
        </div>
        <div className="h-3 w-12 bg-surface-disabled rounded" />
      </div>

      {/* 4 MetricCards: icon + bold number + label */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-surface rounded-lg border border-line p-3 text-center"
          >
            {/* Icon row */}
            <div className="flex items-center justify-center mb-1">
              <div className="h-4 w-4 bg-surface-disabled rounded" />
            </div>
            {/* Value */}
            <div className="h-7 w-12 bg-surface-disabled rounded mx-auto mb-1" />
            {/* Label */}
            <div className="h-3 w-16 bg-surface-raised rounded mx-auto" />
          </div>
        ))}
      </div>

      {/* Chart section: "MONTHLY TRENDS" label + tabs + chart area */}
      <div>
        <div className="h-3 w-28 bg-surface-disabled rounded mb-2" />
        {/* Tab row: Deals | Emails | Value */}
        <div className="flex gap-1 mb-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-7 w-14 bg-surface-disabled rounded" />
          ))}
        </div>
        {/* Bar chart area — matches h-56 */}
        <div className="h-56 bg-surface-disabled rounded" />
      </div>
    </div>
  </div>
)

/**
 * Summary metric card displaying a single performance KPI with tooltip.
 *
 * Used to show key totals like deals opened, deals won, win rate, and emails sent.
 */
interface MetricCardProps {
  /** Icon element displayed above the value. */
  icon: React.ReactNode
  /** Formatted metric value (e.g., "1,234" or "65.5%"). */
  value: string
  /** Short label describing the metric. */
  label: string
  /** Title shown in the tooltip popup. */
  tooltipTitle: string
  /** Detailed description shown in the tooltip popup. */
  tooltipContent: string
}

const MetricCard: React.FC<MetricCardProps> = ({
  icon,
  value,
  label,
  tooltipTitle,
  tooltipContent,
}) => (
  <Tooltip
    title={tooltipTitle}
    content={tooltipContent}
    placement="top"
    delay={[300, 0]}
  >
    <div className="bg-surface rounded-lg border border-line p-3 text-center cursor-help">
      <div className="flex items-center justify-center gap-1.5 text-fg-secondary mb-1">
        {icon}
      </div>
      <div className="text-xl font-bold text-fg leading-tight">{value}</div>
      <div className="text-xs text-fg-secondary mt-0.5">{label}</div>
    </div>
  </Tooltip>
)

/**
 * Performance dashboard showing historical metrics over time.
 *
 * Layout:
 * 1. Header with range selector and period toggle (Monthly/Weekly)
 * 2. Summary metrics for selected range (Deals Opened, Won, Win Rate, Value Opened, Value Won, Emails Sent)
 * 3. Bar chart with tabs (Deals, Emails, Value) showing trends over the selected time window
 */
export const PerformanceDashboardCard: React.FC<
  PerformanceDashboardCardProps
> = ({ userId, className }) => {
  const [stats, setStats] = useState<PerformanceStats | null>(null)
  const [pipelineValueStats, setPipelineValueStats] =
    useState<PipelineValueStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [periodType] = useState<PeriodType>('monthly')
  const [range, setRange] = useState<PerformanceRange>('6m')
  // Three tabs swap between the headline summary, the pipeline-by-
  // stage breakdown, and the trends chart. Summary is the default —
  // it's the at-a-glance view most users land on the home page for.
  type SectionTab = 'summary' | 'pipeline' | 'trends'
  const [activeSection, setActiveSection] = useState<SectionTab>('summary')

  // Pipeline-by-stage breakdown — sourced from the homepage-stats endpoint.
  // Same query key as the (now-removed) PipelineSnapshotCard so any other
  // caller of getHomepageStats reuses the cache entry.
  const { data: pipelineStats } = useQuery<HomepageStats | null>({
    queryKey: ['homepage-stats', userId ?? 'self'],
    queryFn: () => getHomepageStats(userId),
  })

  const pipelineChartItems = useMemo(() => {
    if (!pipelineStats) return []
    const stagesWithDeals = pipelineStats.deals_by_stage.filter(
      (s) => s.count > 0
    )
    const totalDeals = stagesWithDeals.reduce((sum, s) => sum + s.count, 0)
    return stagesWithDeals.map((stage, index) => ({
      name: `${stage.stage_name} · ${formatCurrency(stage.total_value)} · ${stage.count} deal${stage.count !== 1 ? 's' : ''}`,
      value: stage.count,
      color: getChartColor(index),
      percentage:
        totalDeals > 0 ? Math.round((stage.count / totalDeals) * 100) : 0,
    }))
  }, [pipelineStats])
  useEffect(() => {
    let isCancelled = false

    const fetchStats = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // Pipeline value stats are fetched independently so a failure
        // doesn't break the rest of the dashboard — the Pipeline tab
        // simply won't appear.
        const perfData = await getPerformanceStats({
          userId,
          periodType,
          range,
        })
        if (!isCancelled) {
          setStats(perfData)
        }
      } catch (err) {
        if (!isCancelled) {
          const message =
            err instanceof Error
              ? err.message
              : 'Failed to load performance data'
          setError(message)
        }
      }

      try {
        const pipelineValueData = await getPipelineValueStats({
          userId,
          periodType,
          range,
        })
        if (!isCancelled) {
          setPipelineValueStats(pipelineValueData)
        }
      } catch {
        // Non-critical — Pipeline tab won't appear
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchStats()

    return () => {
      isCancelled = true
    }
  }, [userId, periodType, range])

  if (isLoading) {
    return <PerformanceDashboardSkeleton className={className} />
  }

  if (error) {
    return (
      <div className={cn(CARD_CONTAINER_STYLES, 'p-6', className)}>
        <div className="text-center text-fg-secondary">
          <p>Unable to load performance data</p>
          <p className="text-sm text-fg-muted mt-1">{error}</p>
        </div>
      </div>
    )
  }

  if (!stats) {
    return null
  }

  const { totals, range_info, periods } = stats

  const metricIconStyle = { color: '#97b98f' }

  const performanceMetrics = [
    {
      key: 'deals-opened',
      label: 'Deals Opened',
      icon: <TrendingUp size={16} style={metricIconStyle} />,
      value: formatInteger(totals.deals_opened),
      tooltipTitle: 'Deals Opened',
      tooltipContent: `Number of deals opened in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'deals-won',
      label: 'Deals Won',
      icon: <Trophy size={16} style={metricIconStyle} />,
      value: formatInteger(totals.deals_won),
      tooltipTitle: 'Deals Won',
      tooltipContent: `Deals closed won in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'value-won',
      label: 'Value Won',
      icon: <CircleDollarSign size={16} style={metricIconStyle} />,
      value: formatCurrency(totals.value_won),
      tooltipTitle: 'Value Won',
      tooltipContent: `Annual value of deals won in ${range_info.range_label.toLowerCase()}`,
    },
    {
      key: 'win-rate',
      label: 'Win Rate',
      icon: <Goal size={16} style={metricIconStyle} />,
      value:
        totals.overall_win_rate !== null
          ? `${formatPercentage(totals.overall_win_rate)}%`
          : '-',
      tooltipTitle: 'Win Rate',
      tooltipContent: `Win rate for ${range_info.range_label.toLowerCase()} (Won / (Won + Lost))`,
    },
  ]

  return (
    <div className={cn(CARD_CONTAINER_STYLES, className)}>
      {/* Header row — section tabs on the left, range selector on the
          right. Tabs use the same FbUI/Tabs styling as settings pages
          (border-bottom strip with underlined active tab). One section
          is visible at a time; the pipeline tab is suppressed when
          there's nothing to show in it. */}
      <div className="flex items-end justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <Tabs<SectionTab>
            tabs={[
              { key: 'summary', title: 'Your Performance' },
              ...(pipelineChartItems.length > 0
                ? [
                    {
                      key: 'pipeline' as SectionTab,
                      title: 'Pipeline by stage',
                    },
                  ]
                : []),
              {
                key: 'trends',
                title: `${periodType === 'monthly' ? 'Monthly' : 'Weekly'} Trends`,
              },
            ]}
            selectedTab={activeSection}
            setSelectedTab={setActiveSection}
          />
        </div>
        <div className="flex-shrink-0 pb-1">
          <RangeSelector value={range} onChange={setRange} />
        </div>
      </div>

      {activeSection === 'summary' && (
        <div className="grid grid-cols-4 gap-3">
          {performanceMetrics.map((metric) => (
            <MetricCard
              key={metric.key}
              icon={metric.icon}
              value={metric.value}
              label={metric.label}
              tooltipTitle={metric.tooltipTitle}
              tooltipContent={metric.tooltipContent}
            />
          ))}
        </div>
      )}

      {activeSection === 'pipeline' && pipelineChartItems.length > 0 && (
        <div className="bg-surface rounded-lg border border-line p-3">
          <ProgressChart
            label="Pipeline by stage"
            items={pipelineChartItems}
            tooltipValueFormatter={(item) =>
              `${item.value} deal${item.value !== 1 ? 's' : ''}`
            }
            legend={{
              columns: 2,
              showPercentages: true,
            }}
          />
        </div>
      )}

      {activeSection === 'trends' && (
        <PerformanceChart
          periods={periods}
          pipelinePeriods={pipelineValueStats?.periods}
        />
      )}
    </div>
  )
}
